<template>
  <div
    :class="{
      'main-nav-sub': true,
      'is-visible': isVisible,
    }"
    role="none"
  >
    <transition name="fade">
      <div v-show="isVisible" class="main-nav-sub__inner container" role="none">
        <div class="main-nav-sub__cols" role="none">
          <div class="main-nav-sub__items">
            <div class="main-nav-sub__items-cols">
              <div ref="subMenuContainerRef" class="main-nav-sub__items-col is-parent">
                <div
                  v-for="(child, index) in childLinks"
                  :key="`child-item-${index}`"
                  :class="[
                    'main-nav-sub__link-wrapper',
                    {
                      'is-active': activeIndex === index,
                      'has-sub-links': child.sub_links && child.sub_links.length,
                    },
                  ]"
                  role="none"
                  :aria-expanded="activeIndex === index"
                >
                  <div class="main-nav-sub__link-inner">
                    <MegaMenuItemLink
                      :item="child"
                      has-arrow
                      class="main-nav-sub__link"
                      label-class="main-nav-sub__link-label"
                      :post-icon-class="{
                        'main-nav-sub__icon': true,
                        'link-primary__arrow': true,
                        'is-always': true,
                        'has-sub-links': child.sub_links && child.sub_links.length,
                      }"
                      role="menuitem"
                      tabindex="0"
                      @link-click="handleLinkClick(child.sub_links && child.sub_links.length, $event)"
                    />
                  </div>
                  <ul :class="['main-nav-sub__items-links', { 'is-active': activeIndex === index }]" role="menu">
                    <li v-for="(subLink, subIndex) in child.sub_links" :key="`child-sub-item-${subIndex}`" class="main-nav-sub__item" role="none">
                      <MegaMenuItemLink
                        :item="subLink"
                        class="main-nav-sub__item-link link-secondary"
                        role="menuitem"
                        tabindex="0"
                        @link-click="$emit('link-click', $event)"
                      >
                        <template #post>
                          <bard v-if="subLink.description" :as-content="true" :content="subLink.description" />
                        </template>
                      </MegaMenuItemLink>
                    </li>
                    <li class="main-nav-sub__item-button">
                      <pt-button
                        v-if="child.cta_button && child.cta_button.link"
                        size="extra-small"
                        v-bind="getButtonBindings(child.cta_button)"
                        @click="$emit('link-click', $event)"
                      >
                        {{ child.cta_button.label }}
                      </pt-button>
                    </li>
                  </ul>
                </div>
                <div v-if="secondaryChildLinks && secondaryChildLinks.length" class="main-nav-sub__secondary-items-inner">
                  <p class="main-nav-sub__secondary-title">Other</p>
                  <div
                    v-for="(secondaryChild, index) in secondaryChildLinks"
                    :key="`secondary-child-item-${index}`"
                    role="none"
                    class="main-nav-sub__secondary-link-wrapper"
                  >
                    <MegaMenuItemLink
                      :item="secondaryChild"
                      class="main-nav-sub__secondary-link link-secondary"
                      role="menuitem"
                      tabindex="0"
                      @link-click="$emit('link-click', $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div :class="bannerClasses">
            <picture-basic
              v-if="banner.image"
              class="main-nav-sub__banner-fig"
              v-bind="getPictureBindings(banner.image, { ratio: null, preset: 'cover' })"
            />
            <div class="main-nav-sub__banner-content">
              <h4 v-if="banner.title" class="main-nav-sub__banner-title">{{ banner.title }}</h4>
              <bard v-if="banner.text" class="main-nav-sub__banner-text" :as-content="true" :content="banner.text" />
              <pt-button
                v-if="banner.cta_button && banner.cta_button.link"
                class="main-nav-sub__banner-button"
                size="extra-small"
                v-bind="getButtonBindings(banner.cta_button)"
                @click="$emit('link-click', $event)"
              >
                {{ banner.cta_button.label }}
              </pt-button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import MegaMenuItemLink from '~/components/ui/layout/mega-menu/MegaMenuItemLink.vue';
import Bard from '~/components/content/Bard.vue';
import PtButton from '~/components/global/elements/PtButton.vue';
import PictureBasic from '~/components/global/images/PictureBasic.vue';
import { useButtons } from '~/composables/buttons';
import { useImages } from '~/composables/images';
import { getSelectValue } from '~/utils/blocks';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['link-click', 'close']);
let menuAimInstance: ReturnType<typeof useMenuAim> | null = null;
const subMenuContainerRef = ref<HTMLElement | null>(null);
const { getButtonBindings } = useButtons();
const { getPictureBindings } = useImages();

watch(
  () => props.isVisible,
  (open) => {
    if (open) {
      activeIndex.value = defaultIndex;
      return;
    }
    activeIndex.value = null;
  }
);

onMounted(() => {
  const elements: HTMLElement[] = subMenuContainerRef.value
    ? Array.from(subMenuContainerRef.value.children).filter(
        (n): n is HTMLElement => n instanceof HTMLElement && n.classList.contains('main-nav-sub__link-wrapper')
      )
    : [];

  menuAimInstance = useMenuAim(elements, {
    exitDelay: 300,
    interval: 50,
    sensitivity: 7,
    onEnter: (el) => {
      if (!el.classList.contains('has-sub-links')) {
        activeIndex.value = null;
        return;
      }
      activeIndex.value = elements.indexOf(el);
    },
    onExit: (el) => {
      if (!el.classList.contains('has-sub-links')) {
        activeIndex.value = 0;
      }
    },
  });
});

onUnmounted(() => {
  if (menuAimInstance) menuAimInstance.destroy();
});

const childLinks = computed(() => {
  return Array.isArray(props.item.child_links) ? props.item.child_links : [];
});

const secondaryChildLinks = computed(() => {
  return Array.isArray(props.item.secondary_child_links) ? props.item.secondary_child_links : [];
});

const banner = computed(() => {
  return props.item.banner || null;
});

const defaultIndex = childLinks.value[0]?.sub_links ? 0 : null;
const activeIndex = ref<number | null>(defaultIndex);
const opacityOptions = banner.value?.opacity_options;

const bannerClasses = computed(() => {
  const classes = ['main-nav-sub__banner'];

  if (!banner.value) {
    return classes;
  }

  if (banner.value.image) {
    classes.push('has-image');
  }

  if (opacityOptions && opacityOptions.enabled) {
    classes.push('is-opacity');

    const overlayColor = opacityOptions.overlay_color ? getSelectValue(opacityOptions.overlay_color, 'value', 'cream') : 'cream';
    const overlayOpacity = opacityOptions.overlay_percentage ? getSelectValue(opacityOptions.overlay_percentage, 'value', 30) : 30;
    const textColor = opacityOptions.text_color ? getSelectValue(opacityOptions.text_color, 'value', 'blue') : 'blue';
    const textColorMobile = opacityOptions.text_color_mobile ? getSelectValue(opacityOptions.text_color_mobile, 'value', 'blue') : 'blue';

    classes.push(`is-overlay-${overlayColor}`, `is-overlay-opacity-${overlayOpacity}`, `is-text-${textColor}`, `is-text-mob-${textColorMobile}`);
  }

  if (banner.value.gradient_overlay_percentage) {
    const gradientOpacity = getSelectValue(banner.value.gradient_overlay_percentage, 'value', 0);
    classes.push('is-gradient');
    classes.push(`is-gradient-opacity-${gradientOpacity}`);
  }

  return classes;
});

function handleLinkClick(hasChild: true, payload: any) {
  if (hasChild) return;
  emit('link-click', payload);
}
</script>
